import React from "react";
import { Column } from "primereact/column";
import { Row } from "primereact/row";
import type { FundingChartRow } from "../pages/FundingChart";
import InsuredTable from "app/uicomponents/macrocomponents/InteractiveProposal/Funding/InsuredTable";

type FundingRow = FundingChartRow;

const PRIMARY = "#1E3A8A";

const fmt = (v: number | null | undefined): string =>
  v == null
    ? "-"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(v);

// Maps filter IDs (from Funding.tsx) to table column fields
const FILTER_FIELD_MAP: Record<string, keyof FundingRow> = {
  CLIENT_PREMIUM: "clientPremium",
  LENDER_PREMIUM: "lenderPremium",
  INTEREST_PAYMENT: "clientInterestPayment",
  PLAN_FEE: "clientPlanFee",
  ORIGINATION_FEE: "clientOriginationFees",
};

interface FundingTableProps {
  data: FundingRow[];
  selectedYear: number;
  insuredName: string;
  isDark: boolean;
  cardBg: string;
  // divider: string;
  // textMain: string;
  showProjections?: boolean;
  textSub: string;
  activeFilters: Set<string>;
  isSplitDollar?: boolean;
}

const FundingTable: React.FC<FundingTableProps> = ({
  data,
  selectedYear,
  insuredName,
  isDark,
  cardBg,
  // divider,
  // textMain,
  showProjections,
  textSub,
  activeFilters,
  isSplitDollar = false,
}) => {
  const thBg = isDark ? "#3A3A50" : "#EBEBEF";
  const grayColor = isDark ? "#6B7280" : "#9CA3AF";
  const colStyle = (filterId?: string, isProjected = false): React.CSSProperties | undefined => {
    if (!filterId) return undefined;
    if (isProjected && !showProjections) return { color: grayColor, opacity: 0.5 };
    const field = FILTER_FIELD_MAP[filterId];
    if (!field) return undefined;
    if (activeFilters.has(filterId)) return undefined;
    return { color: grayColor, opacity: 0.5 };
  };

  const colHeader = (label: string, filterId?: string): React.ReactNode => {
    const isShowProjections = label.includes("Projected") && !showProjections;
    if (!isShowProjections || !filterId || activeFilters.has(filterId)) return label;
    return <span style={{ color: grayColor, opacity: 0.5 }}>{label}</span>;
  };

  const splitDollarColumns: any[] = [
    {
      field: "planYear",
      header: "Plan Year",
      formatter: (_: any, __: any, row: FundingRow) => (
        <span className={row.year === selectedYear ? "font-bold" : ""}>{row.planYear}</span>
      ),
    },
    {
      field: "clientPremium",
      header: "Employee Premium",
      formatter: (v: number) => fmt(v),
    },
    {
      field: "clientInterestPayment",
      header: "Employee Interest Payment",
      formatter: (v: number) => fmt(v),
    },
    {
      field: "lenderOriginationFees",
      header: "Employee Principal Payment",
      formatter: (v: number) => fmt(v),
    },
    {
      field: "lenderPremium",
      header: "Employer Premium",
      formatter: (v: number) => fmt(v),
    },
    {
      field: "clientPlanFee",
      header: "Plan Fees",
      formatter: (v: number) => fmt(v),
    },
    {
      field: "clientOriginationFees",
      header: "Origination Fees",
      formatter: (v: number) => fmt(v),
    },
    {
      field: "totalPolicyPremium",
      header: "Total Policy Premium",
      formatter: (v: number) => <span className="font-bold">{fmt(v)}</span>,
    },
  ];

  const defaultColumns: any[] = [
    {
      field: "planYear",
      header: "Plan Year",
      formatter: (_: any, __: any, row: FundingRow) => (
        <span className={row.year === selectedYear ? "font-bold" : ""}>{row.planYear}</span>
      ),
    },
    {
      field: "year",
      header: "Year",
      formatter: (_: any, __: any, row: FundingRow) => (
        <span className={row.year === selectedYear ? "font-bold" : ""}>{row.year}</span>
      ),
    },

    // Client Premium
    {
      field: "clientPremium",
      header: colHeader("Client Premium", "CLIENT_PREMIUM"),
      style: colStyle("CLIENT_PREMIUM"),
      formatter: (v: number) => fmt(v),
    },
    {
      field: "clientPremiumIllustrated",
      header: colHeader("Projected Client Premium", "CLIENT_PREMIUM"),
      style: colStyle("CLIENT_PREMIUM", true),
      formatter: (v: number) => fmt(v),
    },

    // Plan Fee
    {
      field: "clientPlanFee",
      header: colHeader("Plan Fees", "PLAN_FEE"),
      style: colStyle("PLAN_FEE"),
      formatter: (v: number) => fmt(v),
    },
    {
      field: "clientPlanFeeIllustrated",
      header: colHeader("Projected Plan Fees", "PLAN_FEE"),
      style: colStyle("PLAN_FEE", true),
      formatter: (v: number) => fmt(v),
    },

    // Interest Payment
    {
      field: "clientInterestPayment",
      header: colHeader("Interest Payment", "INTEREST_PAYMENT"),
      style: colStyle("INTEREST_PAYMENT"),
      formatter: (v: number) => fmt(v),
    },
    {
      field: "clientInterestPaymentIllustrated",
      header: colHeader("Projected Interest Payment", "INTEREST_PAYMENT"),
      style: colStyle("INTEREST_PAYMENT", true),
      formatter: (v: number) => fmt(v),
    },

    // Origination Fees
    {
      field: "clientOriginationFees",
      header: colHeader("Origination Fees", "ORIGINATION_FEE"),
      style: colStyle("ORIGINATION_FEE"),
      formatter: (v: number) => fmt(v),
    },
    {
      field: "clientOriginationFeesIllustrated",
      header: colHeader("Projected Origination Fees", "ORIGINATION_FEE"),
      style: colStyle("ORIGINATION_FEE", true),
      formatter: (v: number) => fmt(v),
    },

    // Lender Premium
    {
      field: "lenderPremium",
      header: colHeader("Financed Premium", "LENDER_PREMIUM"),
      style: colStyle("LENDER_PREMIUM"),
      formatter: (v: number) => fmt(v),
    },
    {
      field: "lenderPremiumIllustrated",
      header: colHeader("Projected Financed Premium", "LENDER_PREMIUM"),
      style: colStyle("LENDER_PREMIUM", true),
      formatter: (v: number) => fmt(v),
    },

    {
      field: "lenderOriginationFees",
      // header: "Financing Fees",
      header: colHeader("Financing Fees", "PROJECT_FINANCING_FEES"),
      style: colStyle("PROJECT_FINANCING_FEES"),
      formatter: (v: number) => fmt(v),
    },
    {
      field: "lenderOriginationFeesIllustrated",
      // header: "Projected Financing Fees",
      header: colHeader("Projected Financing Fees", "PROJECT_FINANCING_FEES"),
      style: colStyle("PROJECT_FINANCING_FEES", true),
      formatter: (v: number) => fmt(v),
    },

    {
      field: "totalPolicyPremium",
      header: "Total Policy Premium",
      formatter: (v: number) => <span className="font-bold">{fmt(v)}</span>,
    },
  ];

  const columns = isSplitDollar ? splitDollarColumns : defaultColumns;

  const columnGroupRow = isSplitDollar ? (
    <Row>
      <Column header="" colSpan={1} style={{ background: thBg }} align="center" />
      <Column
        header="Employee Out of Pocket Payments"
        colSpan={3}
        style={{ background: thBg }}
        align="center"
      />
      <Column
        header="Employer Out of Pocket Payments"
        colSpan={3}
        style={{ background: thBg }}
        align="center"
      />
      <Column
        header="Total Premium Paid to Insurance Company"
        colSpan={1}
        style={{ background: thBg }}
        align="center"
      />
    </Row>
  ) : (
    <Row>
      <Column header="" colSpan={2} style={{ background: thBg }} align="center" />
      <Column header="Client Payments" colSpan={9} style={{ background: thBg }} align="center" />
      <Column header="Financed Payments" colSpan={3} style={{ background: thBg }} align="center" />
      <Column
        header="Total Premium Paid to Insurance Company"
        colSpan={1}
        style={{ background: thBg }}
        align="center"
      />
    </Row>
  );

  return (
    <div className={`rounded-2xl shadow-md overflow-hidden ${cardBg}`}>
      <InsuredTable
        data={data}
        columns={columns}
        hoverColor={PRIMARY}
        columnGroup={columnGroupRow}
        scrollable
        scrollHeight="500px"
      />
      {insuredName && (
        <p className={`text-[11px] text-center py-2 ${textSub}`}>Plan Funding for {insuredName}</p>
      )}
    </div>
  );
};

export default FundingTable;
